class LetoileNativePlp {
  constructor(root = document) {
    this.sections = [...root.querySelectorAll('[data-le-plp]')]
      .filter((section) => !section.dataset.lePlpBound);

    this.sections.forEach((section) => {
      section.dataset.lePlpBound = 'true';
      const trigger = section.querySelector('[data-le-plp-facet-button]');
      const facets = section.querySelector('[data-responsive-facets]');
      const facetSearch = section.querySelector('[data-facet-search]');
      const facetSearchClear = section.querySelector('[data-facet-search-clear]');
      const facetForm = section.querySelector('[data-collection-filter-form], [data-search-filter-form]');
      const categoryTrigger = section.querySelector('[data-le-plp-category-button]');
      const brandTrigger = section.querySelector('[data-le-plp-brand-button]');
      const deliveryTriggers = [...section.querySelectorAll('[data-fast-delivery-toggle]')];
      const mobileQuery = window.matchMedia('(max-width: 989px)');
      const sortMenu = section.querySelector('[data-plp-sort-menu]');
      const sortForm = section.querySelector('[data-collection-sort-form]');
      const sortInput = section.querySelector('[data-plp-sort-input]');
      const sortControls = [...section.querySelectorAll('[data-plp-sort-control]')];
      const showAllFilters = section.querySelector('[data-show-all-filters]');
      const productKey = (card) => card?.dataset.discountEngineProductId
        || card?.dataset.discountEngineProductHandle
        || card?.dataset.productCardVariantId
        || '';

      section.addEventListener('click', async (event) => {
        const loadMore = event.target.closest('[data-listing-load-more]');
        if (!loadMore || !section.contains(loadMore)) return;
        event.preventDefault();
        if (loadMore.dataset.listingLoadMoreBusy === 'true') return;

        const grid = section.querySelector('[data-listing-product-grid]');
        const continuation = loadMore.closest('[data-listing-continuation]');
        if (!grid || !continuation || !loadMore.href) return;

        const requestUrl = loadMore.dataset.listingSourceUrl || loadMore.href;

        loadMore.dataset.listingLoadMoreBusy = 'true';
        loadMore.setAttribute('aria-disabled', 'true');
        continuation.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(requestUrl, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) throw new Error(`Load more request failed with ${response.status}`);

          const nextDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextGrid = nextDocument.querySelector('[data-listing-product-grid]');
          const nextContinuation = nextDocument.querySelector('[data-listing-continuation]');
          if (!nextGrid || !nextContinuation) throw new Error('Load more response is missing listing content');

          const existingKeys = new Set([...grid.querySelectorAll('[data-product-card-root]')]
            .map(productKey)
            .filter(Boolean));
          const nextCards = [...nextGrid.querySelectorAll(':scope > [data-product-card-root]')];
          const appendedCards = nextCards.filter((card) => {
            const key = productKey(card);
            if (!key || existingKeys.has(key)) return false;
            existingKeys.add(key);
            return true;
          });
          if (!appendedCards.length) throw new Error('Load more response does not contain new products');

          const fragment = document.createDocumentFragment();
          appendedCards.forEach((card) => fragment.append(document.adoptNode(card)));
          grid.append(fragment);
          grid.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));

          const nextLoadMore = nextContinuation.querySelector('[data-listing-load-more]');
          if (nextLoadMore) {
            const displayPathname = loadMore.dataset.listingDisplayPathname;
            if (displayPathname) {
              const nextSourceUrl = new URL(nextLoadMore.href, window.location.origin);
              nextLoadMore.dataset.listingSourceUrl = `${nextSourceUrl.pathname}${nextSourceUrl.search}${nextSourceUrl.hash}`;
              nextLoadMore.dataset.listingDisplayPathname = displayPathname;
              nextSourceUrl.pathname = displayPathname;
              nextLoadMore.href = `${nextSourceUrl.pathname}${nextSourceUrl.search}${nextSourceUrl.hash}`;
            }
            loadMore.replaceWith(document.adoptNode(nextLoadMore));
          } else {
            loadMore.remove();
          }
          continuation.dataset.listingPage = nextContinuation.dataset.listingPage || '';
          continuation.dataset.listingPages = nextContinuation.dataset.listingPages || '';
          section.dispatchEvent(new CustomEvent('letoile:listing-appended', {
            bubbles: true,
            detail: {
              appended: appendedCards.length,
              page: Number.parseInt(nextContinuation.dataset.listingPage || '0', 10),
            },
          }));
        } catch (error) {
          loadMore.removeAttribute('aria-disabled');
          loadMore.dataset.listingLoadMoreBusy = 'false';
          section.dispatchEvent(new CustomEvent('letoile:listing-append-error', {
            bubbles: true,
            detail: { error },
          }));
        } finally {
          continuation.removeAttribute('aria-busy');
        }
      });

      if (sortMenu) {
        const sortTrigger = sortMenu.querySelector('[data-plp-sort-trigger]');
        document.addEventListener('click', (event) => {
          if (sortMenu.open && !sortMenu.contains(event.target)) sortMenu.open = false;
        });
        sortMenu.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          sortMenu.open = false;
          sortTrigger?.focus();
        });
      }

      sortControls.forEach((control) => {
        const eventName = control.matches('select') ? 'change' : 'click';
        control.addEventListener(eventName, () => {
          if (!sortForm || !sortInput || !control.value) return;
          sortInput.value = control.value;
          sortMenu?.removeAttribute('open');
          sortForm.requestSubmit();
        });
      });

      section.querySelectorAll('[data-scheduled-surface]').forEach((surface) => {
        const startAt = Date.parse(surface.dataset.activeFrom || '');
        const endAt = Date.parse(surface.dataset.activeUntil || '');
        const now = Date.now();
        const active = (Number.isNaN(startAt) || now >= startAt) && (Number.isNaN(endAt) || now <= endAt);
        surface.hidden = !(active || window.Shopify?.designMode);
      });

      section.querySelectorAll('.le-plp__campaign input').forEach((input) => {
        input.addEventListener('change', () => input.form?.requestSubmit());
      });

      section.querySelectorAll('[data-rail-previous], [data-rail-next]').forEach((button) => {
        button.addEventListener('click', () => {
          const railId = button.dataset.railPrevious || button.dataset.railNext;
          const rail = railId ? document.getElementById(railId) : null;
          if (!rail) return;
          const direction = button.dataset.railPrevious ? -1 : 1;
          const rtlDirection = document.documentElement.dir === 'rtl' ? -direction : direction;
          rail.scrollBy({ left: rtlDirection * Math.max(rail.clientWidth * 0.75, 240), behavior: 'smooth' });
        });
      });

      const syncResponsiveFacets = () => {
        if (facets) facets.open = !mobileQuery.matches;
      };

      const syncFacetModalState = () => {
        document.body.classList.toggle(
          'le-plp-filter-open',
          Boolean(mobileQuery.matches && facets?.open),
        );
      };

      const closeExpandedFacet = (group, restoreFocus = true) => {
        if (!group) return;
        group.removeAttribute('data-values-expanded');
        facets?.removeAttribute('data-desktop-expanded');
        document.body.classList.remove('le-plp-expanded-filter-open');
        if (restoreFocus) group.querySelector('[data-facet-show-all]')?.focus();
      };

      syncResponsiveFacets();
      syncFacetModalState();
      mobileQuery.addEventListener?.('change', () => {
        const expandedGroup = section.querySelector('[data-facet-group][data-values-expanded]');
        closeExpandedFacet(expandedGroup, false);
        syncResponsiveFacets();
        syncFacetModalState();
      });
      facets?.addEventListener('toggle', syncFacetModalState);

      trigger?.addEventListener('click', () => {
        if (!facets) return;
        facets.open = true;
        syncFacetModalState();
      });

      categoryTrigger?.addEventListener('click', () => {
        const category = section.querySelector('.le-plp__category');
        const categoryFacet = [...section.querySelectorAll('[data-facet-group]')].find((group) => {
          const param = group.dataset.facetParam?.toLocaleLowerCase() || '';
          const label = group.querySelector('[data-facet-label]')?.textContent.toLocaleLowerCase() || '';
          return param.includes('category') || param.includes('product_type') || label.includes('categor');
        });

        if (category) {
          category.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }

        if (facets) facets.open = true;
        if (categoryFacet) categoryFacet.open = true;
        (categoryFacet || facets)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      brandTrigger?.addEventListener('click', () => {
        const brandFacet = [...section.querySelectorAll('[data-facet-group]')].find((group) => {
          const param = group.dataset.facetParam?.toLocaleLowerCase() || '';
          const label = group.querySelector('[data-facet-label]')?.textContent.toLocaleLowerCase() || '';
          return param.includes('vendor') || label.includes('brand');
        });

        if (!facets) return;
        facets.open = true;
        syncFacetModalState();
        if (brandFacet) brandFacet.open = true;
        (brandFacet || facets).scrollIntoView({ behavior: 'auto', block: 'start' });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const expandedGroup = section.querySelector('[data-facet-group][data-values-expanded]');
        if (expandedGroup) {
          closeExpandedFacet(expandedGroup);
          return;
        }
        if (mobileQuery.matches && facets?.open) {
          facets.open = false;
          trigger?.focus();
        }
      });

      section.querySelectorAll('[data-facet-show-all]').forEach((button) => {
        button.addEventListener('click', () => {
          const group = button.closest('[data-facet-group]');
          if (!group) return;
          group.setAttribute('data-values-expanded', '');
          document.body.classList.add('le-plp-expanded-filter-open');
          if (!mobileQuery.matches) {
            facets?.setAttribute('data-desktop-expanded', '');
          }
          group.querySelector('[data-facet-value-search]')?.focus();
        });
      });

      showAllFilters?.addEventListener('click', () => {
        section.querySelectorAll('[data-facet-group]').forEach((group) => {
          group.open = true;
          group.removeAttribute('hidden');
          group.setAttribute('data-values-visible', '');
        });
      });

      section.querySelectorAll('[data-facet-expanded-close]').forEach((button) => {
        button.addEventListener('click', () => {
          const group = button.closest('[data-facet-group]');
          closeExpandedFacet(group);
        });
      });

      section.querySelectorAll('[data-facet-expanded-choose]').forEach((button) => {
        button.addEventListener('click', () => {
          const group = button.closest('[data-facet-group]');
          if (mobileQuery.matches) {
            closeExpandedFacet(group);
            return;
          }
          if (!facetForm) return;
          facetForm.requestSubmit();
        });
      });

      section.querySelectorAll('[data-facet-value-search]').forEach((input) => {
        input.addEventListener('input', () => {
          const query = input.value.trim().toLocaleLowerCase();
          const group = input.closest('[data-facet-group]');
          group?.querySelectorAll('[data-facet-value]').forEach((value) => {
            value.hidden = query !== '' && !value.textContent.toLocaleLowerCase().includes(query);
          });
        });
      });

      facetForm?.querySelectorAll('[data-facet-value-input]').forEach((input) => {
        input.addEventListener('change', () => {
          if (mobileQuery.matches || input.closest('[data-facet-group][data-values-expanded]')) return;
          facetForm.requestSubmit();
        });
      });

      facetForm?.querySelectorAll('[data-facet-price-min], [data-facet-price-max]').forEach((input) => {
        input.addEventListener('change', () => {
          if (!mobileQuery.matches) facetForm.requestSubmit();
        });
      });

      section.querySelectorAll('[data-facet-price-slider]').forEach((slider) => {
        const group = slider.closest('[data-facet-group]');
        const fieldMin = group?.querySelector('[data-facet-price-min]');
        const fieldMax = group?.querySelector('[data-facet-price-max]');
        const rangeMin = slider.querySelector('[data-facet-range-min]');
        const rangeMax = slider.querySelector('[data-facet-range-max]');
        if (!fieldMin || !fieldMax || !rangeMin || !rangeMax) return;

        const numeric = (value, fallback) => {
          const parsed = Number.parseFloat(value);
          return Number.isFinite(parsed) ? parsed : fallback;
        };
        const syncRanges = () => {
          rangeMin.value = String(numeric(fieldMin.value, numeric(rangeMin.min, 0)));
          rangeMax.value = String(numeric(fieldMax.value, numeric(rangeMax.max, 0)));
        };
        const syncFields = (source) => {
          const minValue = numeric(rangeMin.value, 0);
          const maxValue = numeric(rangeMax.value, numeric(rangeMax.max, 0));
          if (minValue > maxValue) {
            if (source === rangeMin) rangeMin.value = String(maxValue);
            else rangeMax.value = String(minValue);
          }
          fieldMin.value = rangeMin.value === rangeMin.min ? '' : rangeMin.value;
          fieldMax.value = rangeMax.value === rangeMax.max ? '' : rangeMax.value;
        };

        fieldMin.addEventListener('input', syncRanges);
        fieldMax.addEventListener('input', syncRanges);
        rangeMin.addEventListener('input', () => syncFields(rangeMin));
        rangeMax.addEventListener('input', () => syncFields(rangeMax));
        syncRanges();
      });

      deliveryTriggers.forEach((deliveryTrigger) => {
        deliveryTrigger.addEventListener('click', () => {
          if (deliveryTrigger.disabled) return;
          const nextState = !deliveryTriggers.some((item) => item.getAttribute('aria-pressed') === 'true');
          deliveryTriggers.forEach((item) => item.setAttribute('aria-pressed', String(nextState)));
          section.dispatchEvent(new CustomEvent('letoile:fast-delivery-change', {
            bubbles: true,
            detail: { enabled: nextState },
          }));
        });
      });

      facetSearch?.addEventListener('input', () => {
        const query = facetSearch.value.trim().toLocaleLowerCase();
        if (facetSearchClear) facetSearchClear.hidden = query === '';
        section.querySelectorAll('[data-facet-group]').forEach((group) => {
          const text = group.textContent.toLocaleLowerCase();
          group.hidden = query !== '' && !text.includes(query);
        });
      });

      facetSearchClear?.addEventListener('click', () => {
        if (!facetSearch) return;
        facetSearch.value = '';
        facetSearch.dispatchEvent(new Event('input', { bubbles: true }));
        facetSearch.focus();
      });
    });
  }
}

new LetoileNativePlp();
document.addEventListener('shopify:section:load', (event) => new LetoileNativePlp(event.target));
